import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import CovidNECalgary from "../Assets/CovidNECalgary.png";
import TeenMentalHealthWorkshop from "../Assets/TeenMentalHealthWorkshop.png";
import SummerCamp2021 from "../Assets/SummerCamp2021.png";
import MothersSummerCamp2021 from "../Assets/MothersSummerCamp2021.png";
import CalgaryCaresThumbnail from "../Assets/CalgaryCaresThumbnail.png";

const ease = [0.22, 1, 0.36, 1];

const fadeInUp = {
  initial: {
    opacity: 0,
    y: 38,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease,
    },
  },
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const posts = [
  {
    title: "Active participation of mothers during summer camp was huge asset",
    date: "January 31, 2022",
    categories: ["Mothers", "Summer Camp"],
    summary:
      "Volunteer moms and sisters were the heart of our summer camp in 2021, showing community unity and healing after lockdown.",
    slug: "mothers-summer-camp",
    image: MothersSummerCamp2021,
    imageAlt: "Support of mothers during summer camp 2021",
    imageCaption: "Support of mothers during summer camp 2021",
    content: [
      "Appreciate the brilliant service of volunteer moms and sisters in general. We never thought the EthioCare Calgary summer camp 2021 would bring us together at this level. We deserved this moment of community after the long lockdown due to the COVID-19 pandemic.",
      "የኢትዮጵያዊያንና የኤርትራዊያንን በጎነት በሚገባ ስላየን ማንም ሁለታችንን በክፉ እንዲያነሳን አያስፈልግም። Ethiocare Calgary summer camp 2021 በፍፁም ባልጠበቅነው መልኩ ድንቅ ፕሮግራም እንዲሆንልን ያደረጉት እህቶቻችን በከፊል።",
      "From organizing meals to mentoring children and supporting staff, these mothers created a foundation of love and generosity that radiated through the camp.",
      "Thank you to all our supporters: Government of Canada, YourAlberta, Calgary Foundation, United Way of Calgary and Area, City of Calgary, Calgary Parks, and Second Harvest.",
    ],
  },
  {
    title: "EthioCare Calgary ran its first summer camp in July 2021",
    date: "January 31, 2022",
    categories: ["Summer Camp"],
    summary:
      "Children ages 6 to 8 practiced drumming with an amazing volunteer during EthioCare Calgary’s first summer camp.",
    slug: "summer-camp-2021",
    image: SummerCamp2021,
    imageAlt: "Drumming during summer camp in 2021",
    imageCaption: "Drumming during summer camp in 2021",
    content: [
      "Children ages 6 to 8 practiced drumming with an amazing professional who volunteered their time to support the EthioCare Calgary summer camp in 2021.",
      "We are now in the second day of the fourth week, and the program, launched for the first time, is going very well. More than 275 children are registered and actively participating.",
      "EthioCare deeply appreciates all parents who take the time to bring their children and youth to the camp. This initiative is taken very seriously as a permanent part of our Family Wellness strategy.",
      "The joy of seeing kids engaged, learning, and having fun without any electronics in their hands is the greatest reward.",
      "Thank you to all our incredible supporters: Government of Canada, YourAlberta, City of Calgary, Calgary Foundation, United Way of Calgary and Area, Second Harvest, TikTok, and our generous public and private donors.",
    ],
  },
  {
    title: "Understanding teen mental health workshops at EthioCare Calgary",
    date: "January 31, 2022",
    categories: [
      "Family Wellness",
      "Mental Health and Wellness",
      "Youth Workshops",
    ],
    summary:
      "EthioCare partnered with Tasha Belix to facilitate interactive mental health workshops for youth and parents.",
    slug: "teen-mental-health-workshops",
    image: TeenMentalHealthWorkshop,
    imageAlt: "Tasha Belix, Registered Psychologist",
    imageCaption: "Tasha Belix, M.A., Registered Psychologist",
    content: [
      "EthioCare, in partnership with Tasha Belix, M.A., EMDR-trained Registered Psychologist specializing in youth and family, organized four mental health awareness and promotion sessions by Zoom. The discussions were participatory, culturally relevant, and linguistically appropriate.",
      "A total of 45 youth and 40 parents completed two sessions per group. Parents requested that Tasha facilitate another session next year so community members can continue building the knowledge needed to support their children’s mental health.",
      "EthioCare has promoted mental health awareness through social media platforms like WhatsApp and Facebook. These efforts have helped us reach a broader population and foster stronger community dialogue around wellness.",
      "Thank you, Tasha, for your incredible presentation and your warm, inclusive approach. We are grateful to our community members, and we look forward to hosting more sessions both in person and on Zoom.",
      "Special thanks to our supporters and funders: City of Calgary, Calgary Foundation, United Way of Calgary and Area, YourAlberta, Second Harvest, Settlement Services in the Bow Valley, SPEC Association, and the Mental Health Commission of Canada.",
    ],
  },
  {
    title: "Disproportionate number of COVID-19 cases in NE Calgary",
    date: "November 23, 2020",
    categories: ["Family Wellness", "Food Security"],
    summary:
      "Calgary doctors and community groups raised concern about the high number of COVID-19 cases in northeast Calgary.",
    slug: "covid-ne-calgary",
    image: CovidNECalgary,
    imageAlt: "NE Calgary COVID-19 community report",
    imageCaption:
      "Community concern around COVID-19 cases in northeast Calgary",
    content: [
      "Calgary doctors and a local community group said more action needed to be taken to address the high number of COVID-19 cases in northeast Calgary.",
      "Alberta Health divided the city into 16 local geographic areas. While most of the rest of the city had cases per 100,000 in the 100 to 200 range, Calgary Upper NE had a much higher reported rate.",
      "Lower NE and Calgary Centre were also areas of concern during that period.",
      "Community advocates highlighted that many people in northeast Calgary were less able to work from home and often lived in multi-generational households, increasing the risk of exposure.",
      "This story reflects the importance of culturally responsive outreach, public health navigation, and trusted community support during times of crisis.",
    ],
  },
  {
    title: "Calgary Cares: Charity EthioCare",
    date: "April 13, 2020",
    categories: ["Family Wellness", "Food Security", "Immigration"],
    summary:
      "EthioCare supported newcomer families facing language, cultural, and pandemic-related barriers during an uncertain time.",
    slug: "calgary-cares-charity",
    image: CalgaryCaresThumbnail,
    imageAlt: "EthioCare Calgary charity support during the pandemic",
    imageCaption:
      "EthioCare supporting families during the COVID-19 pandemic",
    content: [
      "Imagine moving to an entirely new country, not knowing the language and suddenly finding yourself submerged in a strange new culture. Then, not long after arriving, a global health pandemic hits.",
      "Now you are in an unfamiliar place under unprecedented circumstances, with no idea how long they will last or where to go for help.",
      "EthioCare’s work during this period focused on helping families navigate uncertainty, access support, and stay connected to trusted community resources.",
      "This story reflects the importance of culturally responsive care, food access, outreach, and practical support for newcomer families.",
    ],
  },
];

const CategoryLabel = ({ category }) => (
  <span className="inline-flex border-l-2 border-[#B09E29] pl-2 text-[11px] sm:text-xs font-bold uppercase tracking-[0.12em] text-[#7E742A] dark:text-[#D9C84B]">
    {category}
  </span>
);

const BlogCard = ({ post }) => (
  <motion.article
    variants={fadeInUp}
    className="group overflow-hidden border border-[#C8DEDB] bg-[#EAF6F4] shadow-[0_16px_40px_rgba(32,82,78,0.07)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(32,82,78,0.13)] dark:border-white/10 dark:bg-[#191919] dark:shadow-black/30"
  >
    {/* IMAGE */}
    <div className="relative h-56 overflow-hidden bg-[#D9EBE8] dark:bg-[#111111]">
      <img
        src={post.image}
        alt={post.imageAlt}
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-[#EAF6F4] via-transparent to-transparent dark:from-[#191919]" />

      <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent" />
    </div>

    {/* CONTENT */}
    <div className="relative -mt-7 p-6 pt-0">
      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-2">
        {post.categories.map((category) => (
          <CategoryLabel key={category} category={category} />
        ))}
      </div>

      <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-[#8F8120] dark:text-[#CDB937]">
        {post.date}
      </p>

      <h2
        className="mb-4 text-2xl font-extrabold leading-snug text-[#163C3B] transition-colors group-hover:text-[#807316] dark:text-white dark:group-hover:text-[#CDB937]"
        style={{
          textShadow: "0 2px 3px rgba(17,63,61,0.08)",
        }}
      >
        {post.title}
      </h2>

      <p className="mb-6 text-sm leading-relaxed text-[#577371] dark:text-gray-300">
        {post.summary}
      </p>

      <Link
        to={`/blog/${post.slug}`}
        className="inline-flex border-b border-[#9B8C24] pb-1 text-sm font-bold uppercase tracking-[0.1em] text-[#786E25] transition-all duration-300 hover:border-[#163C3B] hover:text-[#163C3B] dark:border-[#CDB937] dark:text-[#CDB937] dark:hover:text-white"
      >
        Continue Reading
      </Link>
    </div>
  </motion.article>
);

const BlogArticle = ({ post }) => (
  <main className="-mt-10 min-h-screen bg-[#F3FBFA] px-6 sm:px-10 lg:px-16 py-20 md:py-28 text-[#163C3B] dark:bg-[#141414] dark:text-white">
    <article className="mx-auto max-w-5xl">
      <motion.div
        initial="initial"
        animate="animate"
        variants={fadeInUp}
      >
        <Link
          to="/blog"
          className="inline-flex border-b border-[#9B8C24] pb-1 text-xs sm:text-sm font-bold uppercase tracking-[0.15em] text-[#786E25] transition-colors hover:text-[#163C3B] dark:border-[#CDB937] dark:text-[#CDB937] dark:hover:text-white"
        >
          Back to Blog
        </Link>
      </motion.div>

      {/* ARTICLE HEADER */}
      <motion.header
        initial="initial"
        animate="animate"
        variants={staggerContainer}
        className="mt-12 mb-12"
      >
        <motion.div
          variants={fadeInUp}
          className="mb-6 flex flex-wrap gap-x-5 gap-y-3"
        >
          {post.categories.map((category) => (
            <CategoryLabel key={category} category={category} />
          ))}
        </motion.div>

        <motion.p
          variants={fadeInUp}
          className="mb-5 text-xs sm:text-sm font-bold uppercase tracking-[0.22em] text-[#8F8120] dark:text-[#CDB937]"
        >
          {post.date}
        </motion.p>

        <motion.h1
          variants={fadeInUp}
          className="max-w-5xl text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-[-0.035em] text-[#163C3B] dark:text-white"
          style={{
            textShadow:
              "0 2px 3px rgba(17,63,61,0.14), 0 8px 22px rgba(17,63,61,0.05)",
          }}
        >
          {post.title}
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="mt-7 max-w-4xl text-lg md:text-xl leading-relaxed text-[#476564] dark:text-gray-300"
        >
          {post.summary}
        </motion.p>
      </motion.header>

      {/* ARTICLE IMAGE */}
      <motion.figure
        initial={{
          opacity: 0,
          y: 35,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          delay: 0.15,
          duration: 0.8,
          ease,
        }}
        className="mb-14"
      >
        <div className="relative max-h-[600px] overflow-hidden bg-[#DCEDEA]">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="max-h-[600px] w-full object-cover"
          />

          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#F3FBFA]/45 via-transparent to-transparent dark:from-[#141414]/45" />
        </div>

        {post.imageCaption && (
          <figcaption className="border-b border-[#B6D3CF] px-1 py-4 text-sm text-[#68817F] dark:border-zinc-700 dark:text-gray-400">
            {post.imageCaption}
          </figcaption>
        )}
      </motion.figure>

      {/* ARTICLE BODY */}
      <motion.div
        initial={{
          opacity: 0,
          y: 35,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          delay: 0.25,
          duration: 0.8,
          ease,
        }}
        className="max-w-4xl"
      >
        <div className="border-t border-[#A7CBC6] dark:border-zinc-700 pt-10 space-y-7">
          {post.content.map((paragraph, index) => (
            <p
              key={index}
              className="text-base md:text-lg leading-8 text-[#476564] dark:text-gray-300"
            >
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-14 border-t border-[#A7CBC6] dark:border-zinc-700 pt-8">
          <Link
            to="/blog"
            className="inline-flex min-h-[52px] items-center justify-center bg-[#D1BD3D] px-7 py-3 text-base font-bold text-black transition-all duration-300 hover:-translate-y-1 hover:bg-[#E0CB4A]"
          >
            More Community Stories
          </Link>
        </div>
      </motion.div>
    </article>
  </main>
);

export default function Blog() {
  const { slug } = useParams();

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  if (slug) {
    const post = posts.find((item) => item.slug === slug);

    if (!post) {
      return (
        <main className="-mt-10 flex min-h-[75vh] items-center justify-center bg-[#F3FBFA] px-6 text-center text-[#163C3B] dark:bg-[#141414] dark:text-white">
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.75,
              ease,
            }}
            className="max-w-xl border-t border-b border-[#A7CBC6] py-12 dark:border-zinc-700"
          >
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#8F8120] dark:text-[#CDB937]">
              Blog
            </p>

            <h1
              className="mb-5 text-4xl font-extrabold text-[#163C3B] dark:text-white"
              style={{
                textShadow: "0 2px 3px rgba(17,63,61,0.1)",
              }}
            >
              Post Not Found
            </h1>

            <p className="mb-8 text-[#577371] dark:text-gray-300">
              The story you are looking for is not available or may have been
              moved.
            </p>

            <Link
              to="/blog"
              className="inline-flex min-h-[52px] items-center justify-center bg-[#D1BD3D] px-7 py-3 text-sm font-bold text-black transition-all duration-300 hover:-translate-y-1 hover:bg-[#E0CB4A]"
            >
              Back to Blog
            </Link>
          </motion.div>
        </main>
      );
    }

    return <BlogArticle post={post} />;
  }

  return (
    <main className="-mt-10 min-h-screen bg-[#F3FBFA] px-6 sm:px-10 lg:px-16 py-20 md:py-28 text-[#163C3B] dark:bg-[#141414] dark:text-white">
      <motion.section
        ref={ref}
        initial="initial"
        animate={inView ? "animate" : "initial"}
        variants={staggerContainer}
        className="mx-auto max-w-7xl"
      >
        {/* BLOG HEADER */}
        <motion.div
          variants={fadeInUp}
          className="relative mb-16 md:mb-20 max-w-5xl"
        >
          <p className="mb-5 text-xs sm:text-sm font-bold uppercase tracking-[0.28em] text-[#8F8120] dark:text-[#CDB937]">
            Community Stories
          </p>

          <h1
            className="text-5xl sm:text-6xl md:text-7xl font-extrabold leading-[1.02] tracking-[-0.045em] text-[#163C3B] dark:text-white"
            style={{
              textShadow:
                "0 2px 3px rgba(17,63,61,0.15), 0 8px 22px rgba(17,63,61,0.06)",
            }}
          >
            Stories from
            <span className="block mt-2 text-[#9A8A1D] dark:text-[#E2CD42]">
              our community.
            </span>
          </h1>

          <p className="mt-7 max-w-3xl text-lg md:text-xl leading-relaxed text-[#476564] dark:text-gray-300">
            Explore stories from our programs, families, youth initiatives,
            wellness work, and community outreach across Calgary.
          </p>

          <div className="mt-10 h-px w-full max-w-4xl bg-[#B6D3CF] dark:bg-zinc-700" />
        </motion.div>

        {/* BLOG GRID */}
        <motion.div
          variants={staggerContainer}
          className="grid grid-cols-1 gap-7 md:grid-cols-2 xl:grid-cols-3"
        >
          {posts.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </motion.div>
      </motion.section>
    </main>
  );
}